import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useEditMode,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

/**
 * Sister Properties – curated nearby communities under the same management.
 *
 * Waterford Place sister communities (editor-specified order). Images prefer
 * each community’s own site hero/featured exterior when available; otherwise
 * the paired RentCafe listing photo from the sister-properties page.
 *
 * Empty slots stay hidden on the live site and show as placeholders in edit mode.
 */

// ─── Slot group definitions (one literal per community — schema extractor) ─

const community1Def: SlotGroupDefinition = {
  label: 'Community 1',
  description:
    'Filled at the property level from the current MH website (or a provided URL). Leave empty on the company theme.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: 'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3/2/5458/1cl-amenities-1(2)_thumbnail.jpg',
      generator: {
        prompt:
          'The photo that belongs to THIS card’s community by name: use the image shown beside this community’s name on the source Sister Properties / Our Communities page, or the hero/featured building exterior from this community’s own website (url field). Match name ↔ image — wrong property is unacceptable. Do NOT pick the 1st/2nd/… image from this property’s photo_gallery, a scraped image list, or any pooled media ordered by index.',
        context:
          'Card-thumb image in the Sister Properties grid (4:3). Must depict the same community as this card’s name field only — not this property’s own gallery and not photos used on cards 2–6.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Resort-style outdoor pool and lounge deck in front of the brick apartment buildings at The Claremont',
      generator: {
        prompt:
          'Descriptive alt text for this sister community’s photo — name what is visible (building exterior, amenity, street view) without marketing fluff.',
        length: '1 short sentence',
      },
    },
    name: { label: 'Community Name', type: 'text', defaultValue: 'The Claremont Apartment Homes' },
    location: {
      label: 'Full Address',
      type: 'text',
      defaultValue: '11909 W. 109th Street, Overland Park, KS 66210',
      description: 'Full street address (street, city, state, ZIP).',
    },
    addressUrl: {
      label: 'Address Map URL',
      type: 'url',
      defaultValue: 'https://www.google.com/maps/search/?api=1&query=11909+W.+109th+Street,+Overland+Park,+KS+66210',
      description: 'Google Maps (or similar) link for the full address — the address text links out here.',
    },
    url: { label: 'Website URL', type: 'url', defaultValue: 'https://www.claremontaptskc.com' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'A sister community listed on this property’s current live website (property_website_url in Multihub), usually on a Sister Properties or Our Communities page. Prefer the geographically closest sister to this property. Fill name, full street address, maps URL, community website URL, listing photo, and alt together. CRITICAL for image: pair it with this card’s name — take the photo shown next to that name on the source page, or the hero from that community’s website URL. Never assign images by list order (first six gallery/scraped images). Do not leave the image empty if the community is filled. Do not invent communities that are not on the source site.',
    context:
      'Card 1 of up to 6 on Sister Properties. If fewer than 6 sisters exist, leave_empty. If more than 6, pick the closest unused one. Do not reuse cards 2–6. When a source URL was provided at the property theme level, use that page instead of guessing. Image must match card 1’s community name/URL, not a sequential gallery pick.',
  },
};

const community2Def: SlotGroupDefinition = {
  label: 'Community 2',
  description:
    'Filled at the property level from the current MH website (or a provided URL). Leave empty on the company theme.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: 'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3/3/1124097/ks_overlandpark_crownechase_p1099049_07_5_thumbnail(1).jpg',
      generator: {
        prompt:
          'The photo that belongs to THIS card’s community by name: use the image shown beside this community’s name on the source Sister Properties / Our Communities page, or the hero/featured building exterior from this community’s own website (url field). Match name ↔ image — wrong property is unacceptable. Do NOT pick the 1st/2nd/… image from this property’s photo_gallery, a scraped image list, or any pooled media ordered by index.',
        context:
          'Card-thumb image in the Sister Properties grid (4:3). Must depict the same community as this card’s name field only — not this property’s own gallery and not photos used on cards 1 or 3–6.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Arched brick leasing office entrance with flower beds at Crowne Chase Apartment Homes',
      generator: {
        prompt:
          'Descriptive alt text for this sister community’s photo — name what is visible (building exterior, amenity, street view) without marketing fluff.',
        length: '1 short sentence',
      },
    },
    name: { label: 'Community Name', type: 'text', defaultValue: 'Crowne Chase Apartment Homes' },
    location: {
      label: 'Full Address',
      type: 'text',
      defaultValue: '11621 W. 118th Terrace, Overland Park, KS 66210',
      description: 'Full street address (street, city, state, ZIP).',
    },
    addressUrl: {
      label: 'Address Map URL',
      type: 'url',
      defaultValue: 'https://www.google.com/maps/search/?api=1&query=11621+W.+118th+Terrace,+Overland+Park,+KS+66210',
      description: 'Google Maps (or similar) link for the full address — the address text links out here.',
    },
    url: { label: 'Website URL', type: 'url', defaultValue: 'https://www.crownechase.com' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'Another unused sister community from this property’s current live website (property_website_url), geographically next-closest after card 1. Fill name, full street address, maps URL, community website URL, listing photo, and alt together. CRITICAL for image: pair it with this card’s name — take the photo shown next to that name on the source page, or the hero from that community’s website URL. Never assign images by list order (first six gallery/scraped images). Do not leave the image empty if the community is filled. Do not invent listings.',
    context:
      'Card 2 of up to 6. Different community from cards 1, 3–6. leave_empty if no second sister exists. Prefer closest unused; use a provided property-level source URL when given. Image must match card 2’s community name/URL, not a sequential gallery pick.',
  },
};

const community3Def: SlotGroupDefinition = {
  label: 'Community 3',
  description:
    'Filled at the property level from the current MH website (or a provided URL). Leave empty on the company theme.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: 'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3/3/1124089/ks_overlandpark_creekside_p1099041_22_5_thumbnail(1).jpg',
      generator: {
        prompt:
          'The photo that belongs to THIS card’s community by name: use the image shown beside this community’s name on the source Sister Properties / Our Communities page, or the hero/featured building exterior from this community’s own website (url field). Match name ↔ image — wrong property is unacceptable. Do NOT pick the 1st/2nd/… image from this property’s photo_gallery, a scraped image list, or any pooled media ordered by index.',
        context:
          'Card-thumb image in the Sister Properties grid (4:3). Must depict the same community as this card’s name field only — not this property’s own gallery and not photos used on cards 1–2 or 4–6.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Two-story brick clubhouse with arched entry and flower beds at Creekside Apartment Homes',
      generator: {
        prompt:
          'Descriptive alt text for this sister community’s photo — name what is visible (building exterior, amenity, street view) without marketing fluff.',
        length: '1 short sentence',
      },
    },
    name: { label: 'Community Name', type: 'text', defaultValue: 'Creekside Apartment Homes' },
    location: {
      label: 'Full Address',
      type: 'text',
      defaultValue: '11920 Oakmont St, Overland Park, KS 66213',
      description: 'Full street address (street, city, state, ZIP).',
    },
    addressUrl: {
      label: 'Address Map URL',
      type: 'url',
      defaultValue: 'https://www.google.com/maps/search/?api=1&query=11920+Oakmont+St,+Overland+Park,+KS+66213',
      description: 'Google Maps (or similar) link for the full address — the address text links out here.',
    },
    url: { label: 'Website URL', type: 'url', defaultValue: 'https://www.creeksideaptskc.com' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'Another unused sister community from this property’s current live website (property_website_url), next-closest after cards 1–2. Fill name, full street address, maps URL, community website URL, listing photo, and alt together. CRITICAL for image: pair it with this card’s name — take the photo shown next to that name on the source page, or the hero from that community’s website URL. Never assign images by list order (first six gallery/scraped images). Do not leave the image empty if the community is filled. Do not invent listings.',
    context:
      'Card 3 of up to 6. Different from cards 1–2 and 4–6. leave_empty if fewer than 3 sisters. Prefer closest unused; use a provided property-level source URL when given. Image must match card 3’s community name/URL, not a sequential gallery pick.',
  },
};

const community4Def: SlotGroupDefinition = {
  label: 'Community 4',
  description:
    'Filled at the property level from the current MH website (or a provided URL). Leave empty on the company theme.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: 'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3/2/5458/04-12401%20w%20120th%20st-1(4).jpg',
      generator: {
        prompt:
          'The photo that belongs to THIS card’s community by name: use the image shown beside this community’s name on the source Sister Properties / Our Communities page, or the hero/featured building exterior from this community’s own website (url field). Match name ↔ image — wrong property is unacceptable. Do NOT pick the 1st/2nd/… image from this property’s photo_gallery, a scraped image list, or any pooled media ordered by index.',
        context:
          'Card-thumb image in the Sister Properties grid (4:3). Must depict the same community as this card’s name field only — not this property’s own gallery and not photos used on cards 1–3 or 5–6.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Staged bedroom with white bedding and yellow throw at Stonebriar Woods Apartment Homes',
      generator: {
        prompt:
          'Descriptive alt text for this sister community’s photo — name what is visible (building exterior, amenity, street view) without marketing fluff.',
        length: '1 short sentence',
      },
    },
    name: { label: 'Community Name', type: 'text', defaultValue: 'Stonebriar Woods Apartment Homes' },
    location: {
      label: 'Full Address',
      type: 'text',
      defaultValue: '12401 W. 120th St, Overland Park, KS 66213',
      description: 'Full street address (street, city, state, ZIP).',
    },
    addressUrl: {
      label: 'Address Map URL',
      type: 'url',
      defaultValue: 'https://www.google.com/maps/search/?api=1&query=12401+W.+120th+St,+Overland+Park,+KS+66213',
      description: 'Google Maps (or similar) link for the full address — the address text links out here.',
    },
    url: { label: 'Website URL', type: 'url', defaultValue: 'https://www.stonebriarwoods.com' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'Another unused sister community from this property’s current live website (property_website_url), next-closest after cards 1–3. Fill name, full street address, maps URL, community website URL, listing photo, and alt together. CRITICAL for image: pair it with this card’s name — take the photo shown next to that name on the source page, or the hero from that community’s website URL. Never assign images by list order (first six gallery/scraped images). Do not leave the image empty if the community is filled. Do not invent listings.',
    context:
      'Card 4 of up to 6. Different from cards 1–3 and 5–6. leave_empty if fewer than 4 sisters. Prefer closest unused; use a provided property-level source URL when given. Image must match card 4’s community name/URL, not a sequential gallery pick.',
  },
};

const community5Def: SlotGroupDefinition = {
  label: 'Community 5',
  description:
    'Filled at the property level from the current MH website (or a provided URL). Leave empty on the company theme.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: 'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3/2/5458/10-12490%20quivira%20rd-10_final.jpg',
      generator: {
        prompt:
          'The photo that belongs to THIS card’s community by name: use the image shown beside this community’s name on the source Sister Properties / Our Communities page, or the hero/featured building exterior from this community’s own website (url field). Match name ↔ image — wrong property is unacceptable. Do NOT pick the 1st/2nd/… image from this property’s photo_gallery, a scraped image list, or any pooled media ordered by index.',
        context:
          'Card-thumb image in the Sister Properties grid (4:3). Must depict the same community as this card’s name field only — not this property’s own gallery and not photos used on cards 1–4 or 6.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Apartment kitchen with granite counters opening to a dining area at Stonebriar Apartment Homes',
      generator: {
        prompt:
          'Descriptive alt text for this sister community’s photo — name what is visible (building exterior, amenity, street view) without marketing fluff.',
        length: '1 short sentence',
      },
    },
    name: { label: 'Community Name', type: 'text', defaultValue: 'Stonebriar Apartment Homes' },
    location: {
      label: 'Full Address',
      type: 'text',
      defaultValue: '12490 Quivira Rd, Overland Park, KS 66213',
      description: 'Full street address (street, city, state, ZIP).',
    },
    addressUrl: {
      label: 'Address Map URL',
      type: 'url',
      defaultValue: 'https://www.google.com/maps/search/?api=1&query=12490+Quivira+Rd,+Overland+Park,+KS+66213',
      description: 'Google Maps (or similar) link for the full address — the address text links out here.',
    },
    url: { label: 'Website URL', type: 'url', defaultValue: 'https://www.stonebriarkc.com' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'Another unused sister community from this property’s current live website (property_website_url), next-closest after cards 1–4. Fill name, full street address, maps URL, community website URL, listing photo, and alt together. CRITICAL for image: pair it with this card’s name — take the photo shown next to that name on the source page, or the hero from that community’s website URL. Never assign images by list order (first six gallery/scraped images). Do not leave the image empty if the community is filled. Do not invent listings.',
    context:
      'Card 5 of up to 6. Different from cards 1–4 and 6. leave_empty if fewer than 5 sisters. Prefer closest unused; use a provided property-level source URL when given. Image must match card 5’s community name/URL, not a sequential gallery pick.',
  },
};

const community6Def: SlotGroupDefinition = {
  label: 'Community 6',
  description:
    'Filled at the property level from the current MH website (or a provided URL). Leave empty on the company theme.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: 'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3/2/5458/pricebros-highlandpark-01.jpg',
      generator: {
        prompt:
          'The photo that belongs to THIS card’s community by name: use the image shown beside this community’s name on the source Sister Properties / Our Communities page, or the hero/featured building exterior from this community’s own website (url field). Match name ↔ image — wrong property is unacceptable. Do NOT pick the 1st/2nd/… image from this property’s photo_gallery, a scraped image list, or any pooled media ordered by index.',
        context:
          'Card-thumb image in the Sister Properties grid (4:3). Must depict the same community as this card’s name field only — not this property’s own gallery and not photos used on cards 1–5.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Clubhouse lounge with sofas, teal chairs, and a botanical mural at Highland Park Apartment Homes',
      generator: {
        prompt:
          'Descriptive alt text for this sister community’s photo — name what is visible (building exterior, amenity, street view) without marketing fluff.',
        length: '1 short sentence',
      },
    },
    name: { label: 'Community Name', type: 'text', defaultValue: 'Highland Park Apartment Homes' },
    location: {
      label: 'Full Address',
      type: 'text',
      defaultValue: '10334 Goddard, Overland Park, KS 66214',
      description: 'Full street address (street, city, state, ZIP).',
    },
    addressUrl: {
      label: 'Address Map URL',
      type: 'url',
      defaultValue: 'https://www.google.com/maps/search/?api=1&query=10334+Goddard,+Overland+Park,+KS+66214',
      description: 'Google Maps (or similar) link for the full address — the address text links out here.',
    },
    url: { label: 'Website URL', type: 'url', defaultValue: 'https://www.highlandparkaptskc.com' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'Another unused sister community from this property’s current live website (property_website_url), next-closest after cards 1–5. Fill name, full street address, maps URL, community website URL, listing photo, and alt together. CRITICAL for image: pair it with this card’s name — take the photo shown next to that name on the source page, or the hero from that community’s website URL. Never assign images by list order (first six gallery/scraped images). Do not leave the image empty if the community is filled. Do not invent listings.',
    context:
      'Card 6 of up to 6. Different from cards 1–5. leave_empty if fewer than 6 sisters. Prefer closest unused; use a provided property-level source URL when given. Image must match card 6’s community name/URL, not a sequential gallery pick.',
  },
};

function hasText(value: string | null | undefined): boolean {
  return Boolean(value && value.trim());
}

function isFilled(community: Record<string, string | null>): boolean {
  return (
    hasText(community.name) ||
    hasText(community.url) ||
    hasText(community.image) ||
    hasText(community.location)
  );
}

function CommunityCardBody({
  community,
  number,
  isEditing,
  filled,
}: {
  community: Record<string, string | null>;
  number: number;
  isEditing: boolean;
  filled: boolean;
}) {
  const href = (community.url || '').trim();
  const name = (community.name || '').trim();
  const location = (community.location || '').trim();
  const addressHref = (community.addressUrl || '').trim();
  const linkLabel = (community.label || 'Visit Community').trim();
  const target = community._target || '_blank';
  const rel = target === '_blank' ? 'noopener noreferrer' : undefined;

  return (
    <article
      className={`sister-card${filled ? '' : ' sister-card--empty'}`}
      aria-label={filled ? undefined : `Community ${number} — click to add details`}
    >
      <div className="sister-card-media">
        {hasText(community.image) ? (
          <VibesitesImage
            url={community.image || ''}
            alt={community.alt || name || `Community ${number}`}
            className="sister-card-image"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          />
        ) : (
          <div className="sister-card-image-placeholder" aria-hidden="true" />
        )}
      </div>

      <div className="sister-card-body">
        <h3 className="sister-card-name">
          {name || (isEditing ? `Community ${number}` : '')}
        </h3>
        {location ? (
          addressHref ? (
            <a
              className="sister-card-location sister-card-location--link"
              href={addressHref}
              target={target}
              rel={rel}
            >
              {location}
            </a>
          ) : (
            <p className="sister-card-location">{location}</p>
          )
        ) : isEditing && !filled ? (
          <p className="sister-card-location sister-card-hint">Add full street address</p>
        ) : null}

        {href ? (
          <a className="sister-card-link" href={href} target={target} rel={rel}>
            {linkLabel}
          </a>
        ) : isEditing ? (
          <span className="sister-card-link sister-card-link--muted">Add website URL</span>
        ) : null}
      </div>
    </article>
  );
}

export function SisterProperties() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();
  const editMode = useEditMode();
  const isEditing = Boolean(editMode?.isEditMode);

  const community1 = useSlotGroup('sisterproperties.community1', community1Def);
  const community2 = useSlotGroup('sisterproperties.community2', community2Def);
  const community3 = useSlotGroup('sisterproperties.community3', community3Def);
  const community4 = useSlotGroup('sisterproperties.community4', community4Def);
  const community5 = useSlotGroup('sisterproperties.community5', community5Def);
  const community6 = useSlotGroup('sisterproperties.community6', community6Def);

  return (
    <section ref={sectionRef} className="reveal-fade-up sister-properties">
      <div className="sister-properties-container">
        <div className="sister-properties-grid">
          {/*
            EditableSlotGroup groupPath must be a string literal for the schema
            linter — keep each wrapper inline rather than mapping a shared path.
          */}
          {(isEditing || isFilled(community1)) && (
            <EditableSlotGroup groupPath="sisterproperties.community1" definition={community1Def}>
              <CommunityCardBody
                community={community1}
                number={1}
                isEditing={isEditing}
                filled={isFilled(community1)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community2)) && (
            <EditableSlotGroup groupPath="sisterproperties.community2" definition={community2Def}>
              <CommunityCardBody
                community={community2}
                number={2}
                isEditing={isEditing}
                filled={isFilled(community2)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community3)) && (
            <EditableSlotGroup groupPath="sisterproperties.community3" definition={community3Def}>
              <CommunityCardBody
                community={community3}
                number={3}
                isEditing={isEditing}
                filled={isFilled(community3)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community4)) && (
            <EditableSlotGroup groupPath="sisterproperties.community4" definition={community4Def}>
              <CommunityCardBody
                community={community4}
                number={4}
                isEditing={isEditing}
                filled={isFilled(community4)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community5)) && (
            <EditableSlotGroup groupPath="sisterproperties.community5" definition={community5Def}>
              <CommunityCardBody
                community={community5}
                number={5}
                isEditing={isEditing}
                filled={isFilled(community5)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community6)) && (
            <EditableSlotGroup groupPath="sisterproperties.community6" definition={community6Def}>
              <CommunityCardBody
                community={community6}
                number={6}
                isEditing={isEditing}
                filled={isFilled(community6)}
              />
            </EditableSlotGroup>
          )}
        </div>

        {isEditing ? (
          <p className="sister-properties-editor-note">
            Property level: generate or fill from the current Multihub website URL
            (or a provided sister-properties page). Use up to six closest communities;
            each card image must match that community’s name (source-page photo or that
            site’s hero) — not the first N gallery images. Full addresses link out via
            Map URL. Empty cards stay hidden on the live site.
          </p>
        ) : null}
      </div>

      <style>{`
        .sister-properties {
          padding: 3.5rem 0 4rem;
          background: #ffffff;
        }
        @media (min-width: 768px) {
          .sister-properties { padding: 5rem 0 5.5rem; }
        }

        .sister-properties-container {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .sister-properties-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .sister-properties-container { padding: 0 2rem; } }

        .sister-properties-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        @media (min-width: 640px) {
          .sister-properties-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (min-width: 1024px) {
          .sister-properties-grid { grid-template-columns: repeat(3, 1fr); gap: 1.75rem; }
        }

        .sister-card {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: #ffffff;
          border: 1px solid #e8e8ea;
          border-radius: var(--vibe-radius-lg, 12px);
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
          transition: box-shadow 0.25s ease, border-color 0.25s ease;
        }
        .sister-card:hover {
          box-shadow: 0 8px 28px rgba(0, 0, 0, 0.08);
          border-color: #dddde1;
        }

        .sister-card--empty {
          border-style: dashed;
          box-shadow: none;
          background: #fafafa;
        }
        .sister-card--empty:hover {
          box-shadow: none;
          border-color: var(--vibe-color-primary);
        }

        .sister-card-media {
          position: relative;
          aspect-ratio: 4 / 3;
          background: #e8e8ea;
          overflow: hidden;
        }

        .sister-card-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .sister-card-image-placeholder {
          width: 100%;
          height: 100%;
          background:
            linear-gradient(135deg, #ececef 0%, #e2e3e6 50%, #ececef 100%);
        }

        .sister-card-body {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
          padding: 1.25rem 1.35rem 1.5rem;
          flex: 1;
        }

        .sister-card-name {
          font-family: var(--vibe-font-heading);
          font-size: 1.125rem;
          font-weight: 700;
          line-height: 1.3;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: var(--vibe-color-text);
          margin: 0;
        }

        .sister-card-location {
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          line-height: 1.5;
          color: var(--vibe-color-text-muted);
          margin: 0;
        }

        .sister-card-location--link {
          display: block;
          color: var(--vibe-color-text-muted);
          text-decoration: none;
        }
        .sister-card-location--link:hover {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        .sister-card-hint {
          font-style: italic;
        }

        .sister-card-link {
          display: inline-flex;
          align-items: center;
          margin-top: auto;
          padding-top: 0.85rem;
          font-family: var(--vibe-font-heading);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: var(--vibe-color-primary);
          text-decoration: none;
        }
        .sister-card-link:hover {
          text-decoration: underline;
        }
        .sister-card-link--muted {
          color: var(--vibe-color-text-muted);
          font-style: italic;
          text-transform: none;
          letter-spacing: 0;
          font-family: var(--vibe-font-body);
          font-weight: 400;
          font-size: 0.85rem;
        }

        .sister-properties-editor-note {
          text-align: center;
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          line-height: 1.6;
          color: var(--vibe-color-text-muted);
          margin: 1.75rem 0 0;
          font-style: italic;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SisterProperties',
  displayName: 'Sister Properties',
  tokens: {},
  slotGroups: {
    'sisterproperties.community1': community1Def,
    'sisterproperties.community2': community2Def,
    'sisterproperties.community3': community3Def,
    'sisterproperties.community4': community4Def,
    'sisterproperties.community5': community5Def,
    'sisterproperties.community6': community6Def,
  },
};

(SisterProperties as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default SisterProperties;
